import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Alert, AlertDescription } from "../ui/alert";
import { Spinner } from "../ui/spinner";
import { X, Folder } from "lucide-react";
import { ControlService } from "../../services/controlService";
import { SubCategoryService } from "../../services/subCategoryService";
import { BusinessUnitService } from "../../services/businessUnitService";
import { Badge } from "../ui/badge";
import { useUser } from "../../contexts/UserContext";

const CreateControlForm = ({
  organizationId,
  businessUnitId,
  onClose,
  onControlCreated,
  getAccessTokenSilently,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    location: "",
    frequency: "",
    is_key_control: false,
    is_automated: false,
    is_preventive: true,
    organization_id: organizationId,
    business_unit_id: businessUnitId,
    sub_category_id: "",
    custom_id_prefix: "",
    assignee: "",
  });
  const [subCategories, setSubCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState("");
  const { isOrgAdmin } = useUser();

  const frequencyOptions = [
    { value: "ad-hoc", label: "Ad-hoc" },
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "annually", label: "Annually" },
  ];

  useEffect(() => {
    loadFormData();
  }, []);

  const loadFormData = async () => {
    try {
      setLoadingData(true);
      // Load sub-categories with their parent categories
      const subCategoriesData = await SubCategoryService.getSubCategories(
        organizationId,
        getAccessTokenSilently
      );

      setSubCategories(subCategoriesData.sub_categories || []);

      // Fetch assignable users using the new consolidated endpoint
      const assignableUsersResponse =
        await BusinessUnitService.getAssignableUsers(
          organizationId,
          businessUnitId,
          getAccessTokenSilently
        );

      setUsers(assignableUsersResponse.users || []);
    } catch (err) {
      setError("Failed to load form data");
      console.error(err);
    } finally {
      setLoadingData(false);
    }
  };

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Group sub-categories by their parent category for better UX
  const groupedSubCategories = subCategories.reduce((acc, subCategory) => {
    const categoryName = subCategory.Category?.name || "Uncategorized";
    if (!acc[categoryName]) {
      acc[categoryName] = [];
    }
    acc[categoryName].push(subCategory);
    return acc;
  }, {});

  // Get custom ID preview
  const getCustomIdPreview = () => {
    const selectedSubCategory = subCategories.find(
      (sc) => sc.id === formData.sub_category_id
    );
    if (!selectedSubCategory) return "";

    const prefix = formData.custom_id_prefix || selectedSubCategory.id_prefix;
    return `${prefix}-C001`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!formData.name.trim()) {
      setError("Control name is required");
      return;
    }
    if (!formData.frequency) {
      setError("Frequency is required");
      return;
    }
    if (!formData.sub_category_id) {
      setError("Sub-category is required");
      return;
    }

    try {
      setLoading(true);

      // Prepare data for API
      const submitData = {
        ...formData,
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        location: formData.location.trim() || undefined,
        // Only org admins can set custom_id_prefix
        custom_id_prefix: isOrgAdmin
          ? formData.custom_id_prefix.trim() || undefined
          : undefined,
        // Remove assignee if "none" is selected
        assignee: formData.assignee === "none" ? undefined : formData.assignee,
      };

      const response = await ControlService.createControl(
        submitData,
        getAccessTokenSilently
      );
      onControlCreated(response.control);
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <div className="flex justify-center items-center py-8">
            <Spinner size="lg" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Control</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <Alert className="border-red-200 bg-red-50">
              <AlertDescription className="text-red-800">
                {error}
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 gap-6">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Control Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="e.g., Multi-factor Authentication, Daily System Backup"
                disabled={loading}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                placeholder="Enter a detailed description of the control..."
                rows={3}
                disabled={loading}
              />
            </div>

            {/* Location and Frequency */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) =>
                    handleInputChange("location", e.target.value)
                  }
                  placeholder="e.g., IT Systems, Data Center"
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="frequency">Frequency *</Label>
                <Select
                  value={formData.frequency}
                  onValueChange={(value) =>
                    handleInputChange("frequency", value)
                  }
                  disabled={loading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    {frequencyOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Control Properties */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="is_key_control">Key Control</Label>
                <div className="flex items-center space-x-2 h-10">
                  <Switch
                    id="is_key_control"
                    checked={formData.is_key_control}
                    onCheckedChange={(checked) =>
                      handleInputChange("is_key_control", checked)
                    }
                    disabled={loading}
                  />
                  <Label htmlFor="is_key_control" className="text-body-small">
                    Key control
                  </Label>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="is_automated">Automated</Label>
                <div className="flex items-center space-x-2 h-10">
                  <Switch
                    id="is_automated"
                    checked={formData.is_automated}
                    onCheckedChange={(checked) =>
                      handleInputChange("is_automated", checked)
                    }
                    disabled={loading}
                  />
                  <Label htmlFor="is_automated" className="text-body-small">
                    Automated
                  </Label>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="is_preventive">Control Type</Label>
                <div className="flex items-center space-x-2 h-10">
                  <Switch
                    id="is_preventive"
                    checked={formData.is_preventive}
                    onCheckedChange={(checked) =>
                      handleInputChange("is_preventive", checked)
                    }
                    disabled={loading}
                  />
                  <Label htmlFor="is_preventive" className="text-body-small">
                    {formData.is_preventive ? "Preventive" : "Detective"}
                  </Label>
                </div>
              </div>
            </div>

            {/* Sub-Category Selection - REQUIRED */}
            <div className="space-y-2">
              <Label htmlFor="sub_category_id">Sub-Category *</Label>
              <Select
                value={formData.sub_category_id}
                onValueChange={(value) =>
                  handleInputChange("sub_category_id", value)
                }
                disabled={loading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select sub-category" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(groupedSubCategories).map(
                    ([categoryName, subCats]) => (
                      <div key={categoryName}>
                        <div className="px-2 py-1.5 text-sm font-medium text-muted-foreground bg-muted">
                          {categoryName}
                        </div>
                        {subCats.map((subCategory) => (
                          <SelectItem
                            key={subCategory.id}
                            value={subCategory.id}
                          >
                            <div className="flex items-center justify-between w-full gap-4">
                              <span>{subCategory.name}</span>
                              <span className="text-xs text-muted-foreground shrink-0">
                                {subCategory.id_prefix}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </div>
                    )
                  )}
                </SelectContent>
              </Select>
              <div className="text-caption text-muted-foreground">
                Controls must be assigned to a sub-category for organization and
                custom ID generation.
              </div>
            </div>

            {/* Custom ID Preview */}
            {formData.sub_category_id && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-body-small font-medium text-blue-900">
                  Generated Custom ID Preview
                </p>
                <p className="text-lg font-mono text-blue-700">
                  {getCustomIdPreview()}
                </p>
                <p className="text-caption text-blue-600 mt-1">
                  This control will be assigned a unique custom ID for easy
                  reference
                </p>
              </div>
            )}

            {/* Optional Custom Prefix Override - Only for Org Admins */}
            {isOrgAdmin && (
              <div className="space-y-2">
                <Label htmlFor="custom_id_prefix">
                  Custom ID Prefix (Optional)
                </Label>
                <Input
                  id="custom_id_prefix"
                  value={formData.custom_id_prefix}
                  onChange={(e) =>
                    handleInputChange(
                      "custom_id_prefix",
                      e.target.value.toUpperCase()
                    )
                  }
                  placeholder="e.g., FWALL, BACKUP"
                  disabled={loading}
                  maxLength={10}
                />
                <div className="text-caption text-muted-foreground">
                  Override the default sub-category prefix. Must be 1-10
                  characters, alphanumeric only.
                </div>
              </div>
            )}
            {/* If not org admin, do not render the input and ensure value is not settable */}

            {/* Assignee */}
            <div className="space-y-2">
              <Label htmlFor="assignee">Assignee</Label>
              <Select
                value={formData.assignee}
                onValueChange={(value) => handleInputChange("assignee", value)}
                disabled={loading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select assignee (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <span className="text-muted-foreground">No assignee</span>
                  </SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span>{user.name || user.email}</span>
                          {user.role === "business_unit_head" && (
                            <Badge variant="outline" className="text-xs">
                              Head
                            </Badge>
                          )}
                          {user.role === "organization_admin" && (
                            <Badge variant="secondary" className="text-xs">
                              Admin
                            </Badge>
                          )}
                          {user.role === "business_user" && (
                            <Badge variant="outline" className="text-xs">
                              User
                            </Badge>
                          )}
                        </div>
                        <div className="flex flex-col text-caption text-muted-foreground">
                          {user.email && user.name && <span>{user.email}</span>}
                          {user.title && <span>{user.title}</span>}
                          {user.business_unit_name && (
                            <span className="text-xs">
                              {user.business_unit_name}
                            </span>
                          )}
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={loading}
              className="flex items-center gap-2"
            >
              {loading && <Spinner size="sm" />}
              Create Control
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateControlForm;
